import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { LayerArchitecture } from './LayerArchitecture';
import { useLanguage } from '../../context/LanguageContext';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

export const HeroSection = () => {
  const { t, language } = useLanguage();
  const heroData = t('hero');

  const rotatingWords = language === 'id'
    ? [heroData.headlineHighlight, 'pilot GenAI', 'proyek LLM', 'transformasi AI']
    : [heroData.headlineHighlight, 'GenAI pilots', 'LLM projects', 'AI transformations'];

  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [language, rotatingWords.length]);

  const scrollToDemo = () => {
    document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToUseCases = () => {
    document.querySelector('#usecases')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen pt-24 sm:pt-28 pb-16 sm:pb-20 overflow-hidden bg-zinc-950"
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 grid-background opacity-50" />
      <div className="absolute inset-0 hero-glow" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="relative z-10"
          >

            {/* Headline */}
            <motion.h1
              variants={itemVariants}
              data-testid="hero-headline"
              className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] mb-6 text-zinc-100"
            >
              {heroData.headline}{' '}
              <span className="font-bold inline-grid overflow-visible">
                <AnimatePresence initial={false}>
                  <motion.span
                    key={wordIndex}
                    initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -40, filter: 'blur(8px)' }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="col-start-1 row-start-1"
                  >
                    <span className="gradient-text pr-2 pb-1 inline-block">
                      {rotatingWords[wordIndex]}
                    </span>
                  </motion.span>
                </AnimatePresence>
              </span>{' '}
              {heroData.headlineEnd}
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={itemVariants}
              data-testid="hero-subheadline"
              className="text-base sm:text-lg text-zinc-400 leading-relaxed mb-8 max-w-xl"
            >
              {heroData.subheadline}
            </motion.p>

            {/* CTAs */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-3 mb-10"
            >
              <Button
                data-testid="hero-cta-demo"
                onClick={scrollToDemo}
                className="px-6 py-3 rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2 group"
              >
                {heroData.ctaPrimary}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>

            {/* Proof Chips */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-2"
            >
              {heroData.chips.map((chip, index) => (
                <span
                  key={index}
                  data-testid={`proof-chip-${index}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-950 border border-zinc-800 text-xs text-zinc-400 shadow-sm"
                >
                  <Check className="w-3 h-3 text-blue-500" />
                  {chip}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* Right - Layer Architecture Animation */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <LayerArchitecture />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
